import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import { api__getFinancePostData } from "@/apis/freeFinance/api__getFinancePostData";
import ReservedPostException from "@/exceptions/postExceptions/ReservedPostException";
import api__createArib from "@/apis/arib/api__createArib";
import api__aribSendOtp from "@/apis/arib/api__aribSendOtp";
import api__verifyAribOtp from "@/apis/arib/api__verifyAribOtp";
import useLocalStorage from "@hooks/useLocalStorage";
import api__deleteFinanceOrder from "@/apis/freeFinance/api__deleteFinanceOrder";
import { useLang } from "@/common/Contexts/LanguageContext";
import arib_bank_list_ar from "@data/lookups/arib_bank_list_ar.json";
import arib_bank_list_en from "@data/lookups/arib_bank_list_en.json";
import arib_work_list_ar from "@data/lookups/arib_work_list_ar.json";
import arib_work_list_en from "@data/lookups/arib_work_list_en.json";
import useHelpers from "@hooks/useHelpers";
import api__createAribOrder from "@/apis/arib/api__createAribOrder";
import { trackEvents } from "@/common/events/events";
var extractAndValidationQueryParams = function extractAndValidationQueryParams() {
  var searchParams = new URLSearchParams(__IS_CSR__ ? window.location.search : "");
  var financestep = Number(searchParams.get("financestep")) || 1;
  var post_id = Number(searchParams.get("post_id"));
  var order_id = Number(searchParams.get("order_id"));
  var ref = searchParams.get("ref") === 'aribForm' ? 'aribForm' : null;
  return {
    post_id: post_id,
    financestep: financestep,
    ref: ref
  };
};
var AribFinanceContext = /*#__PURE__*/createContext(null);
export function useAribFinance() {
  return useContext(AribFinanceContext);
}

// @ts-ignore
export var AribFinanceContextProvider = function AribFinanceContextProvider(_ref) {
  var children = _ref.children;
  var _useTranslation = useTranslation(["web_checkout_finance"]),
    t = _useTranslation.t;
  var lang = useLang();
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useSession = useSession(),
    sessionUser = _useSession.user;
  var _useState = useState(null),
    fetching_error = _useState[0],
    setFetching_error = _useState[1];
  var _useState2 = useState(true),
    fetch_loading = _useState2[0],
    setFetch_loading = _useState2[1];
  var _useLocalStorage = useLocalStorage(false),
    getLocalStorage = _useLocalStorage.getLocalStorage,
    setLocalStorage = _useLocalStorage.setLocalStorage,
    removeLocalStorage = _useLocalStorage.removeLocalStorage;
  var _useState3 = useState(null),
    postData = _useState3[0],
    setPostData = _useState3[1];
  var _useHelpers2 = useHelpers(),
    createUrlAbsolute = _useHelpers2.createUrlAbsolute;
  var _useState4 = useState(null),
    installmentCalculatorData = _useState4[0],
    setInstallmentCalculatorData = _useState4[1];
  var queryParams = extractAndValidationQueryParams();
  var _useState5 = useState(queryParams.financestep),
    highestStepReached = _useState5[0],
    setHighestStepReached = _useState5[1];
  var _useState6 = useState(function () {
      var defaultUserInfo = {
        whatsapp_optin: 1,
        first_name: null,
        fathers_name: null,
        family_name: null,
        birth_date: null,
        birth_date_calendar_type: "G",
        mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null,
        city_id: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.city_id) || null,
        gender: null,
        id_number: null
      };
      var stored = getLocalStorage("arib_userInfo");
      if (!stored) return defaultUserInfo;
      var parsedStored = JSON.parse(stored);

      // Create merged object with non-null values from storage
      return _objectSpread(_objectSpread({}, defaultUserInfo), Object.fromEntries(Object.entries(parsedStored).filter(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
          _ = _ref3[0],
          value = _ref3[1];
        return value !== null && value !== "";
      })));
    }),
    userInfo = _useState6[0],
    setUserInfo = _useState6[1];
  var _useState7 = useState(function () {
      var stored = getLocalStorage("arib_workInfo");
      return stored ? JSON.parse(stored) : {
        work_sector: null,
        salary: null,
        salary_transfer_method: null,
        employer_name: null,
        termsAccepted: false,
        other_financial_liabilities: null,
        traffic_fine: null,
        total_financial_liabilities: 0,
        traffic_fine_value: 0
      };
    }),
    workInfo = _useState7[0],
    setWorkInfo = _useState7[1];
  var _useState8 = useState(function () {
      var stored = getLocalStorage("arib_orderData");
      return stored ? JSON.parse(stored) : {
        order_id: null,
        order_uuid: null,
        transaction_id: null,
        skipToUploadPage: null,
        reachAribOtpStep: false
      };
    }),
    orderData = _useState8[0],
    setOrderData = _useState8[1];
  var paymentProcessingUrl = useMemo(function () {
    return createUrlAbsolute("/process-payment-moyasar-callback", {
      order_id: orderData === null || orderData === void 0 ? void 0 : orderData.order_id,
      post_id: postData === null || postData === void 0 ? void 0 : postData.post_id
    }).replace('localhost:3000', 'https://local.syarah.com');
  }, [orderData === null || orderData === void 0 ? void 0 : orderData.order_id, postData === null || postData === void 0 ? void 0 : postData.post_id, createUrlAbsolute]);
  var _useState9 = useState(function () {
      var stored = getLocalStorage("arib_DataAfterSubmit");
      return stored ? JSON.parse(stored) : {
        postId: null,
        isOrderSubmited: null
      };
    }),
    dataAfterSubmit = _useState9[0],
    setDataAfterSubmit = _useState9[1];

  /*********************/

  useEffect(function () {
    if (queryParams.financestep === 1 && highestStepReached == 7) {
      setUserInfo(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          id_number: null
        });
      });
      setHighestStepReached(queryParams.financestep);
    }
    //disable this area to make back work normal else if user try to return after any steps it navigates to post view
    // if(window.location.href.indexOf("financestep") === -1){
    //     return;
    // }
    // if (queryParams.financestep < highestStepReached && queryParams.financestep !== 5) {
    //     action__clearLocalStorageData()
    //     navigateTo("/cardetail/" + postData?.post_id + "?ref=error-multiple-actions");
    // } else
    if (queryParams.financestep > highestStepReached) {
      setHighestStepReached(queryParams.financestep);
    }
  }, [queryParams.financestep, highestStepReached]);
  useEffect(function () {
    if (queryParams.ref === 'aribForm') {
      history.replaceState(null, "", window.location.pathname + window.location.search.replaceAll("&ref=aribForm", '').replaceAll("ref=aribForm", ''));
      setUserInfo(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null
        });
      });
    }
  }, [sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number]); // this will only trigger when userInfo changes on session

  // // Save userInfo
  useEffect(function () {
    setLocalStorage("arib_userInfo", JSON.stringify(userInfo));
  }, [userInfo]);

  // Save workInfo
  useEffect(function () {
    setLocalStorage("arib_workInfo", JSON.stringify(workInfo));
  }, [workInfo]);

  // Save orderData
  useEffect(function () {
    setLocalStorage("arib_orderData", JSON.stringify(orderData));
  }, [orderData]);
  useEffect(function () {
    var _location;
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return;
    }
    var dataInSession = sessionStorage.getItem("installment-calculator-".concat(postData === null || postData === void 0 ? void 0 : postData.post_id));
    var urlParams = new URLSearchParams((_location = location) === null || _location === void 0 ? void 0 : _location.search);
    var encodedData = urlParams.get('finance_calculator_data');
    if (encodedData) {
      var decodedData = decodeURIComponent(encodedData);
      try {
        var parsedData = JSON.parse(decodedData);
        setInstallmentCalculatorData(parsedData);
        // Persist to sessionStorage
        sessionStorage.setItem("installment-calculator-".concat(postData === null || postData === void 0 ? void 0 : postData.post_id), JSON.stringify(parsedData));
        return;
      } catch (error) {
        console.error("Failed to parse finance_calculator_data:", error);
      }
    }
    if (dataInSession) {
      var installmentCalculatorSessionData = JSON.parse(dataInSession);
      setInstallmentCalculatorData(_objectSpread({}, installmentCalculatorSessionData));
    }

    // Listen for custom calculator update events
    var handleCalculatorUpdate = function handleCalculatorUpdate(event) {
      if (event.detail && event.detail.postId === (postData === null || postData === void 0 ? void 0 : postData.post_id)) {
        setInstallmentCalculatorData(event.detail.data);
      }
    };
    window.addEventListener('calculatorDataUpdated', handleCalculatorUpdate);
    return function () {
      window.removeEventListener('calculatorDataUpdated', handleCalculatorUpdate);
    };
  }, [postData === null || postData === void 0 ? void 0 : postData.post_id]);
  var action__handleCalculatorDataChange = function action__handleCalculatorDataChange(calculatorData) {
    var _state$postData;
    setInstallmentCalculatorData(calculatorData);
    // Persist to sessionStorage
    sessionStorage.setItem("installment-calculator-".concat(state === null || state === void 0 ? void 0 : (_state$postData = state.postData) === null || _state$postData === void 0 ? void 0 : _state$postData.post_id), JSON.stringify(calculatorData));
  };
  var action__clearLocalStorageData = function action__clearLocalStorageData() {
    removeLocalStorage("arib_userInfo");
    removeLocalStorage("arib_workInfo");
    removeLocalStorage("arib_orderData");
  };
  var action__clearDataAfterSubmit = function action__clearDataAfterSubmit() {
    removeLocalStorage("arib_DataAfterSubmit");
  };
  var action__getPostID = function action__getPostID() {
    return (queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id) + "";
  };
  var action__backButtonClick = function action__backButtonClick() {
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 5) {
      action__clearLocalStorageData();
      action__clearDataAfterSubmit();
      return navigateTo("/cardetail/".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id));
    }
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 2) {
      navigateTo("/finance-apply-arib?post_id=".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id, "&financestep=1"));
      return;
    }
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 1) {
      navigateTo("/pre-checkout/".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id));
      return;
    }
    history.back();
  };
  var action__getCurrentTitle = function action__getCurrentTitle() {
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 1) {
      return t("personal_information_2", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 2) {
      return t("work_information", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 3) {
      return t("validation_required_documents", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 5) {
      return t("validation_required_documents", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 10) {
      return t("financing_eligibility", {
        ns: "web_checkout_finance"
      });
    }
    return ""; // Ensure it always returns a string
  };
  var action__getFinanceStep = function action__getFinanceStep() {
    return queryParams.financestep;
  };
  var action__updateUserInfo = function action__updateUserInfo(updates) {
    setUserInfo(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__updateWorkInfo = function action__updateWorkInfo(updates) {
    setWorkInfo(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__updateOrderInfo = function action__updateOrderInfo(updates) {
    setOrderData(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__updateDataAfterSubmit = function action__updateDataAfterSubmit(updates) {
    setDataAfterSubmit(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__validateUserInfo = function action__validateUserInfo() {
    var requiredFields = ["first_name", "fathers_name", "family_name", "mobile_number", "city_id", "gender", "id_number", "birth_date"];
    var fieldErrorMessages = {
      first_name: t("validation_first_name_required", {
        ns: "web_checkout_finance"
      }),
      fathers_name: t("validation_middle_name_required", {
        ns: "web_checkout_finance"
      }),
      family_name: t("validation_last_name_required", {
        ns: "web_checkout_finance"
      }),
      mobile_number: t("validation_phone_number_required", {
        ns: "web_checkout_finance"
      }),
      city_id: t("validation_city_required", {
        ns: "web_checkout_finance"
      }),
      gender: t("validation_gender_required", {
        ns: "web_checkout_finance"
      }),
      id_number: t("validation_id_number_required", {
        ns: "web_checkout_finance"
      }),
      birth_date: t("validation_date_birth_required", {
        ns: "web_checkout_finance"
      }),
      // If you require whatsapp_optin:
      whatsapp_optin: t("validation_whatsapp_option_required", {
        ns: "web_checkout_finance"
      })
    };
    var errors = {};
    for (var _i = 0, _requiredFields = requiredFields; _i < _requiredFields.length; _i++) {
      var field = _requiredFields[_i];
      if (!userInfo[field]) {
        errors[field] = fieldErrorMessages[field] || "Missing required field: ".concat(field);
      }
    }
    return errors; // empty object if no errors
  };
  var action__validateWorkInfo = function action__validateWorkInfo() {
    var requiredFields = ["work_sector", "salary", "salary_transfer_method", "other_financial_liabilities", "traffic_fine"];
    if (workInfo.other_financial_liabilities !== "0") {
      requiredFields.push("total_financial_liabilities");
    }
    if (workInfo.traffic_fine !== "0") {
      requiredFields.push("traffic_fine_value");
    }
    var fieldErrorMessages = {
      work_sector: t("select_workplace", {
        ns: "web_checkout_finance"
      }),
      salary: t("validation_salary_required", {
        ns: "web_checkout_finance"
      }),
      salary_transfer_method: t("select_salary_payment_method", {
        ns: "web_checkout_finance"
      }),
      employer_name: t("enter_workplace_name", {
        ns: "web_checkout_finance"
      }),
      termsAccepted: t("agree_2", {
        ns: "web_checkout_finance"
      }),
      other_financial_liabilities: t("enter_financial_obligations", {
        ns: "web_checkout_finance"
      }),
      traffic_fine: t("enter_traffic_violations", {
        ns: "web_checkout_finance"
      }),
      total_financial_liabilities: t("enter_total_financial_obligations", {
        ns: "web_checkout_finance"
      }),
      traffic_fine_value: t("enter_total_amount_traffic_fines", {
        ns: "web_checkout_finance"
      })
    };
    var errors = {};
    for (var _i2 = 0, _requiredFields2 = requiredFields; _i2 < _requiredFields2.length; _i2++) {
      var field = _requiredFields2[_i2];
      if (!workInfo[field]) {
        errors[field] = fieldErrorMessages[field] || "Missing required field: ".concat(field);
      }
    }
    return errors; // empty object if no errors
  };
  var fetch__FinancePostData = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      var _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _context.p = 0;
            api__getFinancePostData(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id).then(function (res) {
              var _res$data;
              if ((res === null || res === void 0 ? void 0 : res.status) === 230 || (res === null || res === void 0 ? void 0 : res.status) === 500 || (res === null || res === void 0 ? void 0 : res.status) === 404) {
                return navigateTo("/404");
              }
              var _ref5 = (res === null || res === void 0 ? void 0 : (_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.data) || {},
                details = _ref5.details,
                price = _ref5.price,
                g4Data = _ref5.g4Data;
              // Extract what you need

              var _ref6 = details || {},
                title = _ref6.h1,
                is_chinese = _ref6.is_chinese,
                details_card = _ref6.details_card,
                is_preowned = _ref6.is_preowned;
              var _ref7 = price || {},
                finance_price = _ref7.finance_price;
              var _ref8 = g4Data || {},
                post_condition = _ref8.post_condition;
              var _ref9 = details || {},
                have_finance = _ref9.have_finance,
                is_eligible_for_arib = _ref9.is_eligible_for_arib;
              var post_ID = queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id;
              // Then store exactly what you want:
              setPostData({
                title: title,
                finance_price: finance_price,
                post_condition: post_condition,
                g4Data: g4Data,
                have_finance: have_finance,
                is_eligible_for_arib: is_eligible_for_arib,
                post_id: post_ID,
                is_chinese: is_chinese,
                details_card: details_card,
                is_preowned: is_preowned
              });
            });
            _context.n = 3;
            break;
          case 1:
            _context.p = 1;
            _t = _context.v;
            if (!(_t instanceof ReservedPostException)) {
              _context.n = 2;
              break;
            }
            return _context.a(2, navigateTo("/cardetail/".concat(_t.post_id, "?ref=car_reserved")));
          case 2:
            setFetching_error(_t);
          case 3:
            _context.p = 3;
            setFetch_loading(false);
            return _context.f(3);
          case 4:
            return _context.a(2);
        }
      }, _callee, null, [[0, 1, 3, 4]]);
    }));
    return function fetch__FinancePostData() {
      return _ref4.apply(this, arguments);
    };
  }();
  var fetch__WorkSectorList = /*#__PURE__*/function () {
    var _ref0 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            return _context2.a(2, lang == 'ar' ? arib_work_list_ar : arib_work_list_en);
        }
      }, _callee2);
    }));
    return function fetch__WorkSectorList() {
      return _ref0.apply(this, arguments);
    };
  }();
  var fetch__BanksList = /*#__PURE__*/function () {
    var _ref1 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3() {
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.n) {
          case 0:
            return _context3.a(2, lang == 'ar' ? arib_bank_list_ar : arib_bank_list_en);
        }
      }, _callee3);
    }));
    return function fetch__BanksList() {
      return _ref1.apply(this, arguments);
    };
  }();
  var fetch__createAribJobForm = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4() {
      var params;
      return _regenerator().w(function (_context4) {
        while (1) switch (_context4.n) {
          case 0:
            setFetch_loading(true);
            params = {
              postId: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id,
              workInfo: workInfo,
              order_uuid: (orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid) || null
            };
            return _context4.a(2, api__createArib(params.postId, params.workInfo, params.order_uuid).then(function (res) {
              var _data$data, _data$data2, _data$data3, _data$data4;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              console.log(data, 'data from createAribJobForm');
              action__updateOrderInfo({
                order_uuid: (data === null || data === void 0 ? void 0 : (_data$data = data.data) === null || _data$data === void 0 ? void 0 : _data$data.order_uuid) || (orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid),
                order_id: (data === null || data === void 0 ? void 0 : (_data$data2 = data.data) === null || _data$data2 === void 0 ? void 0 : _data$data2.order_id) || (orderData === null || orderData === void 0 ? void 0 : orderData.order_id),
                payment_enabled: data === null || data === void 0 ? void 0 : (_data$data3 = data.data) === null || _data$data3 === void 0 ? void 0 : _data$data3.payment_enabled,
                amount: data === null || data === void 0 ? void 0 : (_data$data4 = data.data) === null || _data$data4 === void 0 ? void 0 : _data$data4.amount
              });
              return data;
            })["catch"](function (error) {
              console.log(error, 'error');
              setFetching_error(error);
              return error;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee4);
    }));
    return function fetch__createAribJobForm() {
      return _ref10.apply(this, arguments);
    };
  }();
  var fetch__createAribOrder = /*#__PURE__*/function () {
    var _ref11 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee5() {
      var financeData,
        userData,
        params,
        _args5 = arguments;
      return _regenerator().w(function (_context5) {
        while (1) switch (_context5.n) {
          case 0:
            financeData = _args5.length > 0 && _args5[0] !== undefined ? _args5[0] : {};
            setFetch_loading(true);
            userData = _objectSpread(_objectSpread({}, userInfo), financeData || {});
            params = {
              userInfo: userData,
              postId: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id
            };
            return _context5.a(2, api__createAribOrder(params.userInfo, Number(params.postId)).then(function (res) {
              var _data$data5, _data$data6, _data$data7, _data$data8, _data$data9, _data$data0;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              var rangesArray = data !== null && data !== void 0 && (_data$data5 = data.data) !== null && _data$data5 !== void 0 && _data$data5.ranges ? Object.values(data === null || data === void 0 ? void 0 : (_data$data6 = data.data) === null || _data$data6 === void 0 ? void 0 : _data$data6.ranges) : [];
              action__updateOrderInfo({
                order_uuid: data === null || data === void 0 ? void 0 : (_data$data7 = data.data) === null || _data$data7 === void 0 ? void 0 : _data$data7.order_uuid,
                order_id: data === null || data === void 0 ? void 0 : (_data$data8 = data.data) === null || _data$data8 === void 0 ? void 0 : _data$data8.order_id,
                payment_enabled: data === null || data === void 0 ? void 0 : (_data$data9 = data.data) === null || _data$data9 === void 0 ? void 0 : _data$data9.payment_enabled,
                amount: data === null || data === void 0 ? void 0 : (_data$data0 = data.data) === null || _data$data0 === void 0 ? void 0 : _data$data0.amount
              });
              return data;
            })["catch"](function (error) {
              setFetching_error(error);
              console.log(error);
              return null;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee5);
    }));
    return function fetch__createAribOrder() {
      return _ref11.apply(this, arguments);
    };
  }();
  var fetch__AribSendOtop = /*#__PURE__*/function () {
    var _ref12 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee6() {
      var params;
      return _regenerator().w(function (_context6) {
        while (1) switch (_context6.n) {
          case 0:
            setFetch_loading(true);
            params = {
              order_uuid: orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid
            };
            return _context6.a(2, api__aribSendOtp(params.order_uuid).then(function (res) {
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              return data;
            })["catch"](function (error) {
              console.log(error, 'error');
              setFetching_error(error);
              return error;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee6);
    }));
    return function fetch__AribSendOtop() {
      return _ref12.apply(this, arguments);
    };
  }();
  var fetch__VerifyAribOtp = /*#__PURE__*/function () {
    var _ref13 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee7(code) {
      var params, fireAribResponseTrackEvent;
      return _regenerator().w(function (_context7) {
        while (1) switch (_context7.n) {
          case 0:
            setFetch_loading(true);
            params = {
              transaction_id: orderData === null || orderData === void 0 ? void 0 : orderData.transaction_id,
              order_uuid: orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid,
              otp: code
            };
            fireAribResponseTrackEvent = function fireAribResponseTrackEvent(arib_eligibility_status, arib_response_status) {
              var _postData$g4Data, _postData$g4Data2, _postData$g4Data3, _postData$g4Data4;
              trackEvents("arib_response", {
                post_id: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id,
                post_make: postData === null || postData === void 0 ? void 0 : (_postData$g4Data = postData.g4Data) === null || _postData$g4Data === void 0 ? void 0 : _postData$g4Data.post_make,
                post_model: postData === null || postData === void 0 ? void 0 : (_postData$g4Data2 = postData.g4Data) === null || _postData$g4Data2 === void 0 ? void 0 : _postData$g4Data2.post_model,
                post_year: postData === null || postData === void 0 ? void 0 : (_postData$g4Data3 = postData.g4Data) === null || _postData$g4Data3 === void 0 ? void 0 : _postData$g4Data3.post_year,
                inventory_type: postData === null || postData === void 0 ? void 0 : (_postData$g4Data4 = postData.g4Data) === null || _postData$g4Data4 === void 0 ? void 0 : _postData$g4Data4.inventory_type_string,
                post_condition: postData === null || postData === void 0 ? void 0 : postData.post_condition,
                order_id: orderData === null || orderData === void 0 ? void 0 : orderData.order_id,
                user_salary: workInfo === null || workInfo === void 0 ? void 0 : workInfo.salary,
                arib_eligibility_status: arib_eligibility_status,
                arib_response_status: arib_response_status
              });
            };
            return _context7.a(2, api__verifyAribOtp(params.transaction_id, params.order_uuid, params.otp).then(function (res) {
              var _data$data1, _data$data10;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              if (data === null) {
                fireAribResponseTrackEvent("could not check", "fail");
              } else if ((data === null || data === void 0 ? void 0 : (_data$data1 = data.data) === null || _data$data1 === void 0 ? void 0 : _data$data1.code) != "000" || !(data !== null && data !== void 0 && (_data$data10 = data.data) !== null && _data$data10 !== void 0 && _data$data10.isEligible)) {
                fireAribResponseTrackEvent("not eligible", "success");
              } else {
                fireAribResponseTrackEvent("eligible", "success");
              }
              return data;
            })["catch"](function (error) {
              setFetching_error(error);
              return null;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee7);
    }));
    return function fetch__VerifyAribOtp(_x) {
      return _ref13.apply(this, arguments);
    };
  }();
  var fetch__deleteFinanceOrder = /*#__PURE__*/function () {
    var _ref14 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee8(order_uuid) {
      return _regenerator().w(function (_context8) {
        while (1) switch (_context8.n) {
          case 0:
            return _context8.a(2, api__deleteFinanceOrder({
              order_uuid: order_uuid
            }));
        }
      }, _callee8);
    }));
    return function fetch__deleteFinanceOrder(_x2) {
      return _ref14.apply(this, arguments);
    };
  }();

  // const helper__paymentEnabled = () => useCallback(() => {
  //         return orderData?.payment_enabled && orderData?.amount > 0;
  // }, [orderData?.amount, orderData?.payment_enabled]);

  var helper__paymentEnabled = useCallback(function () {
    return (orderData === null || orderData === void 0 ? void 0 : orderData.payment_enabled) && (orderData === null || orderData === void 0 ? void 0 : orderData.amount) > 0;
  }, [orderData === null || orderData === void 0 ? void 0 : orderData.amount, orderData === null || orderData === void 0 ? void 0 : orderData.payment_enabled]);
  var helper__getSalaryRangeFee = function helper__getSalaryRangeFee() {
    var _workInfo$salary, _orderData$ranges;
    var salary = workInfo === null || workInfo === void 0 ? void 0 : (_workInfo$salary = workInfo.salary) === null || _workInfo$salary === void 0 ? void 0 : _workInfo$salary.split(",").join("");
    var salaryRange = orderData === null || orderData === void 0 ? void 0 : (_orderData$ranges = orderData.ranges) === null || _orderData$ranges === void 0 ? void 0 : _orderData$ranges.find(function (range) {
      if (!range.min_salary || !range.max_salary) {
        return false;
      }
      if (salary < (range === null || range === void 0 ? void 0 : range.min_salary) || salary > (range === null || range === void 0 ? void 0 : range.max_salary)) {
        return false;
      }
      if (salary >= (range === null || range === void 0 ? void 0 : range.min_salary) && salary <= (range === null || range === void 0 ? void 0 : range.max_salary)) {
        return true;
      }
    });
    if ((salaryRange === null || salaryRange === void 0 ? void 0 : salaryRange.payment_amount) == 0) {
      return 0;
    }
    return parseInt((salaryRange === null || salaryRange === void 0 ? void 0 : salaryRange.payment_amount) || (orderData === null || orderData === void 0 ? void 0 : orderData.payment_fee_default_value));
  };
  var helper__getAribPaymentType = function helper__getAribPaymentType() {
    if (helpers.helper__paymentEnabled()) {
      return "paid";
    }
    return "free";
  };
  var helper__getFinanceCalculatorData = function helper__getFinanceCalculatorData() {
    var _installmentCalculato, _postData$finance_pri, _installmentCalculato2, _postData$finance_pri2, _installmentCalculato3, _postData$finance_pri3, _installmentCalculato4, _postData$finance_pri4;
    var installmentPeriod = (_installmentCalculato = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.period) !== null && _installmentCalculato !== void 0 ? _installmentCalculato : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri = postData.finance_price) === null || _postData$finance_pri === void 0 ? void 0 : _postData$finance_pri.installment_period;
    var down_payment = (_installmentCalculato2 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.down_payment) !== null && _installmentCalculato2 !== void 0 ? _installmentCalculato2 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri2 = postData.finance_price) === null || _postData$finance_pri2 === void 0 ? void 0 : _postData$finance_pri2.first_payment;
    var last_payment = (_installmentCalculato3 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.last_payment) !== null && _installmentCalculato3 !== void 0 ? _installmentCalculato3 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri3 = postData.finance_price) === null || _postData$finance_pri3 === void 0 ? void 0 : _postData$finance_pri3.last_payment;
    var monthly_installment = (_installmentCalculato4 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.monthly_installment) !== null && _installmentCalculato4 !== void 0 ? _installmentCalculato4 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri4 = postData.finance_price) === null || _postData$finance_pri4 === void 0 ? void 0 : _postData$finance_pri4.text;
    return {
      installmentPeriod: installmentPeriod || 0,
      down_payment: down_payment || 0,
      last_payment: last_payment || 0,
      monthly_installment: monthly_installment || 0
    };
  };

  /** Collections **/
  var actions = {
    action__getPostID: action__getPostID,
    action__backButtonClick: action__backButtonClick,
    action__getCurrentTitle: action__getCurrentTitle,
    action__getFinanceStep: action__getFinanceStep,
    action__updateUserInfo: action__updateUserInfo,
    action__updateWorkInfo: action__updateWorkInfo,
    action__updateOrderInfo: action__updateOrderInfo,
    action__validateUserInfo: action__validateUserInfo,
    action__validateWorkInfo: action__validateWorkInfo,
    action__clearLocalStorageData: action__clearLocalStorageData,
    action__clearDataAfterSubmit: action__clearDataAfterSubmit,
    action__updateDataAfterSubmit: action__updateDataAfterSubmit,
    action__handleCalculatorDataChange: action__handleCalculatorDataChange
  };
  var remote_actions = {
    fetch__FinancePostData: fetch__FinancePostData,
    fetch__WorkSectorList: fetch__WorkSectorList,
    fetch__BanksList: fetch__BanksList,
    fetch__createAribJobForm: fetch__createAribJobForm,
    fetch__AribSendOtop: fetch__AribSendOtop,
    fetch__VerifyAribOtp: fetch__VerifyAribOtp,
    fetch__deleteFinanceOrder: fetch__deleteFinanceOrder,
    fetch__createAribOrder: fetch__createAribOrder
  };
  var helpers = {
    helper__paymentEnabled: helper__paymentEnabled,
    helper__getAribPaymentType: helper__getAribPaymentType,
    helper__getFinanceCalculatorData: helper__getFinanceCalculatorData
  };
  var state = {
    userInfo: userInfo,
    workInfo: workInfo,
    fetching_error: fetching_error,
    fetch_loading: fetch_loading,
    postData: postData,
    orderData: orderData,
    dataAfterSubmit: dataAfterSubmit,
    installmentCalculatorData: installmentCalculatorData
  };
  return /*#__PURE__*/React.createElement(AribFinanceContext.Provider, {
    value: {
      actions: actions,
      remote_actions: remote_actions,
      state: state,
      queryParams: queryParams,
      helpers: helpers
    }
  }, children);
};